/**
 * MessagesPage — admin-only view of transactional email logs.
 *
 * Route: /admin/messages
 * Roles: owner, admin
 *
 * data-testid: candeza-messages-page
 * Table: candeza-messages-table
 * Filter: candeza-messages-status-filter
 * Loading: candeza-messages-loading
 * Empty: candeza-messages-empty
 * Error: candeza-messages-error
 * Row: candeza-messages-row-{index}
 * Status badge: candeza-messages-status-{status}
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { PageContainer } from '@/components/layout';
import { Card } from '@/components/common';
import { useMessages } from '@/api/messages';
import type { MessageLog } from '@/api/messages';

// ── Styled ────────────────────────────────────────────────────────────────────

const FilterRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  align-items: center;
`;

const FilterLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  cursor: pointer;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 10px 12px;
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Td = styled.td`
  padding: 12px;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: middle;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ $status }) =>
    $status === 'sent'
      ? '#dcfce7'
      : $status === 'dev_mock'
      ? '#dbeafe'
      : '#fee2e2'};
  color: ${({ $status }) =>
    $status === 'sent'
      ? '#15803d'
      : $status === 'dev_mock'
      ? '#1d4ed8'
      : '#b91c1c'};
`;

const Loading = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Empty = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ErrorMsg = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  background: #fee2e2;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: #b91c1c;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

// ── Component ──────────────────────────────────────────────────────────────────

const MessagesPage: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const { data, isLoading, isError } = useMessages({
    status: statusFilter || undefined,
  });

  const messages = data?.items ?? [];

  return (
    <PageContainer
      title="Email Message Log"
      subtitle="Transactional emails sent by Candeza ERP"
      data-testid="candeza-messages-page"
    >
      <FilterRow>
        <FilterLabel>Filter by status:</FilterLabel>
        <FilterSelect
          data-testid="candeza-messages-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All</option>
          <option value="dev_mock">Dev Mock</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
        </FilterSelect>
      </FilterRow>

      <Card padding="none">
        {isError && (
          <ErrorMsg data-testid="candeza-messages-error">
            Failed to load message log. Please try again.
          </ErrorMsg>
        )}

        {isLoading && (
          <Loading data-testid="candeza-messages-loading">
            Loading message log…
          </Loading>
        )}

        {!isLoading && !isError && messages.length === 0 && (
          <Empty data-testid="candeza-messages-empty">
            No messages found.{statusFilter ? ` (filter: ${statusFilter})` : ''}
          </Empty>
        )}

        {!isLoading && !isError && messages.length > 0 && (
          <Table data-testid="candeza-messages-table">
            <thead>
              <tr>
                <Th>Status</Th>
                <Th>Template</Th>
                <Th>To</Th>
                <Th>Subject</Th>
                <Th>Sent At</Th>
              </tr>
            </thead>
            <tbody>
              {messages.map((msg: MessageLog, idx: number) => (
                <tr key={msg.message_id} data-testid={`candeza-messages-row-${idx}`}>
                  <Td>
                    <StatusBadge
                      $status={msg.status}
                      data-testid={`candeza-messages-status-${msg.status}`}
                    >
                      {msg.status}
                    </StatusBadge>
                    {msg.error && (
                      <div style={{ fontSize: '11px', color: '#b91c1c', marginTop: 4 }}>
                        {msg.error}
                      </div>
                    )}
                  </Td>
                  <Td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {msg.template_name}
                  </Td>
                  <Td>{msg.to_email}</Td>
                  <Td>{msg.subject}</Td>
                  <Td style={{ whiteSpace: 'nowrap' }}>
                    {new Date(msg.sent_at).toLocaleString()}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </PageContainer>
  );
};

export default MessagesPage;

